import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { LcncAppPicker } from './LcncAppPicker.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface SandboxRow {
  readonly developerId: string;
  readonly userId: string;
  /** The developer, named by the server (G18). */
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly developerRole: string;
  readonly sandboxQuota: number;
  readonly appsCreated: number;
  readonly status: string;
}

const STATUSES = ['', 'ACTIVE', 'SUSPENDED', 'INACTIVE'];

/**
 * M28 — Sandbox Console (§25 / §32). The isolated experimentation environment
 * roster: each enrolled citizen_developer (T376) with their sandbox quota, the
 * number of applications already simulated (appsCreated) and the remaining
 * headroom. Only ACTIVE developers below their quota may simulate a new
 * application in the sandbox — wired to the guarded
 * /v1/lcnc/governance/sandbox/simulate endpoint. Filterable by enrolment status.
 */
export function M28SandboxConsolePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<SandboxRow[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [applicationId, setApplicationId] = useState('');
  const [applicationLabel, setApplicationLabel] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: SandboxRow[] }>(`/v1/lcnc/governance/developers${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, reload]);

  const simulate = async (d: SandboxRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(t('m28.sandbox.validationApplicationId')); return; }
    try {
      await apiRequest(`/v1/lcnc/governance/sandbox/simulate`, {
        method: 'POST',
        body: { developerId: d.developerId, applicationId: applicationId.trim() },
        token,
      });
      setNotice(t('m28.sandbox.noticeSimulated', { appId: applicationLabel, user: personLabel(d.actorName, d.actorCode) ?? '—' }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(
        err instanceof ApiError && err.status === 403
          ? t('m28.sandbox.notAuthorised')
          : (err as Error).message,
      );
    }
  };

  return (
    <main aria-label="sandbox-console">
      <h1>{t('m28.sandbox.title')}</h1>

      <section aria-label="simulate-controls">
        <label>{t('m28.sandbox.fieldApplicationId')}
          <LcncAppPicker label="Application" value={applicationId} onPick={(id, label) => { setApplicationId(id); setApplicationLabel(label); }} />
        </label>
        {actionError && <p role="alert" aria-label="sandbox-error">{actionError}</p>}
        {notice && <p aria-label="sandbox-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m28.sandbox.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m28.sandbox.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m28.sandbox.loading')}</p> : (
        <>
          <p aria-label="sandbox-count">{t('m28.sandbox.count', { count: rows.length })}</p>
          <table aria-label="sandbox-table">
            <thead><tr><th>{t('m28.sandbox.colUser')}</th><th>{t('m28.sandbox.colRole')}</th><th>{t('m28.sandbox.colQuota')}</th><th>{t('m28.sandbox.colUsed')}</th><th>{t('m28.sandbox.colRemaining')}</th><th>{t('m28.sandbox.colStatus')}</th><th>{t('m28.sandbox.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.developerId}>
                  <td>{personLabel(d.actorName, d.actorCode) ?? '—'}</td><td>{d.developerRole}</td><td>{d.sandboxQuota}</td>
                  <td>{d.appsCreated}</td><td>{Math.max(0, d.sandboxQuota - d.appsCreated)}</td>
                  <td>{d.status}</td>
                  <td><button type="button" aria-label={`simulate-${d.userId}`} onClick={() => void simulate(d)}>{t('m28.sandbox.btnSimulate')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
